import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';

import Title from '@vromo/ui/components/Title';
import Alert from '@vromo/ui/components/Alert';
import Select from '@vromo/ui/components/Select2';

import sortBy from 'lodash/sortBy';
import { PolicyCapacity } from '@vromo/models';
import { UnitPicker } from '@vromo/ui/components/UnitPicker';
import CapacityInput from './CapacityInput';
import UrlInput from './UrlInput';
import { getLabelValuePairs } from '@vromo/models/helpers';
import MailToLink from '@vromo/ui/components/MailToLink';
import { ActionButton } from '../components/styled';
import Flex from '@vromo/ui/components/Flex';

type CapacityEditorProps = {
    addCapacity: (capacity: PolicyCapacity) => void;
    cancel: () => void;
    capacity?: PolicyCapacity;
    teamOptions: Array<any>;
};

const loadRejectionLabels = {
    false: 'Notify webhook URL',
    true: 'Reject new jobs',
};

const CapacityEditor = observer(function CapacityEditor({
    addCapacity,
    cancel,
    capacity,
    teamOptions,
}: CapacityEditorProps) {
    const [validationFailed, setValidationFailed] = useState(false);
    const [jobsTeam, setJobsTeam] = useState(capacity?.jobsGroup ?? teamOptions[0]?.value);
    const [loadRejection, setLoadRejection] = useState(capacity?.loadRejection ?? true);
    const [statusMode] = useState(capacity?.statusMode ?? 'unfinished');
    const [notifyUrl, setNotifyUrl] = useState(capacity?.notifyUrl);
    const [vehiclesTeam, setVehiclesTeam] = useState(
        capacity?.vehiclesGroup || teamOptions[0]?.value
    );

    const [hasWorkingCM] = useState(!!capacity?.notifyUrl);
    const [loadFactor, setLoadFactor] = useState(capacity?.maxLoad || 1);

    useEffect(() => {
        const validation = hasWorkingCM ? (loadRejection ? true : !!notifyUrl) : true;
        setValidationFailed(!validation);
    }, [notifyUrl, hasWorkingCM, loadRejection]);

    const saveCapacity = () => {
        addCapacity(
            new PolicyCapacity({
                jobsGroup: jobsTeam,
                loadRejection,
                maxLoad: loadFactor,
                notifyUrl,
                statusMode,
                vehiclesGroup: vehiclesTeam
            })
        );
    };

    return (
        <div style={{ minWidth: 500 }}>
            <Title style={{ margin: 0 }}>Manage Capacity</Title>
            {validationFailed ? (
                <Alert danger>
                    <Flex vertical='middle' horizontal='between'>
                        Make sure inputs are valid before saving.
                    </Flex>
                </Alert>
            ) : null}
            <Flex vertical='middle' style={{  margin: '10px 0px' }}>
                <div style={{ flexGrow: 1 }}>
                    <Select
                        isOptionDisabled={(option: any) => !hasWorkingCM && option.value}
                        value={getLabelValuePairs(loadRejectionLabels, loadRejection.toString())}
                        onChange={(selected: { value: any }) =>
                            setLoadRejection(selected.value === 'true')
                        }
                        options={getLabelValuePairs(loadRejectionLabels)}
                        styles={{
                            container: (styles: any) => ({
                                ...styles,
                                flex: 3,
                                margin: '0px 10px'
                            })
                        }}
                    />
                </div>
                <h3 style={{ flexGrow: 1 }}>when there are</h3>
            </Flex>
            <Flex vertical='middle' horizontal='left' style={{ margin: '10px 0px' }}>
                <CapacityInput
                    load={loadFactor}
                    editLoad={(newLoadFactor: number) => setLoadFactor(newLoadFactor)}
                />
                <UnitPicker
                    style={{
                        margin: '0 10px 0 0'
                    }}>
                    jobs
                </UnitPicker>
                <h3>per available driver</h3>
            </Flex>
            <Flex vertical='middle' style={{ margin: '10px 0 10px 10px' }}>
                <h3>for jobs in</h3>
                <Select
                    value={
                        jobsTeam
                            ? teamOptions.find((t: { value: any }) => t.value === jobsTeam)
                            : null
                    }
                    onChange={(selected: { value: any }) => setJobsTeam(selected.value)}
                    options={sortBy(teamOptions, (t) => t.label)}
                    styles={{
                        container: (styles: any) => ({
                            ...styles,
                            flex: 3,
                            margin: '0px 10px'
                        })
                    }}
                />
                <h3>and drivers in</h3>
                <Select
                    value={
                        vehiclesTeam
                            ? teamOptions.find((t: { value: any }) => t.value === vehiclesTeam)
                            : null
                    }
                    onChange={(selected: { value: any }) => setVehiclesTeam(selected.value)}
                    options={sortBy(teamOptions, (t) => t.label)}
                    styles={{
                        container: (styles: any) => ({
                            ...styles,
                            flex: 3,
                            marginLeft: '10px'
                        })
                    }}
                />
            </Flex>
            {!loadRejection && hasWorkingCM && (
                <Flex vertical='middle' style={{ margin: '10px 0 10px 10px' }}>
                    <h3>Notification webhook url: </h3>
                    <UrlInput url={notifyUrl} editUrl={setNotifyUrl} />
                </Flex>
            )}

            {!hasWorkingCM && (
                <div>
                    To enable notification webhook, please contact{' '}
                    <MailToLink address="support@vromo.io" />.
                </div>
            )}
            <Flex horizontal='right' style={{ marginTop: '40px' }}>
                <ActionButton
                    onClick={() => {
                        cancel();
                    }}>
                    Cancel
                </ActionButton>
                <ActionButton
                    disabled={validationFailed}
                    onClick={saveCapacity}
                    primary
                >
                    Save Capacity
                </ActionButton>
            </Flex>
        </div>
    );
});

export default CapacityEditor;
